import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { DecimalPipe, I18nPluralPipe, NgForOf, NgIf } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { switchMap } from 'rxjs/operators';
import { OrganizationFiltersSidenavComponent, OrganizationFiltersSidenavInput, OrganizationFiltersSidenavOutput } from './organization-filters-sidenav/organization-filters-sidenav.component';
import { SidenavContentService } from '../shared/sidenav/sidenav-content/sidenav-content.service';
import { AppTableComponent, SelectedEvent } from '../shared/table/table.component';
import { EmptyCellDirective, NumberCellDirective } from '../shared/table/table.directive';
import { Organization } from '../../../models/entities/organization';
import { TableTypeNames } from '../../../services/column.service';
import { ColumnMap } from '../../../models/entities/column-map';
import { PropertyManagementCompanyService } from '../../../services/property-management-company/property-management-company.service';
import { ScrollService } from '../../../services/scroll.service';
import { GrowlerService } from '../../../services/growler.service';
import { I18nMap } from '../../../models/entities/i18n-map';
import { PropertyManagementCompanyDataService } from '../../../services/property-management-company/property-manager-company-data.service';
import { AvatarBlockModule } from '../shared/avatar-block/avatar-block.module';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import { EmptyStateModule } from '../shared/empty-state/empty-state.module';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AnimatedDirective } from '../shared/directives/animated.directive';
import { MatChipsModule } from '@angular/material/chips';
import { RouterButtonValue } from '../../../models/entities/router-button-value';
import { AddressLinePipe } from '../../../pipes/format/address-line.pipe';

@UntilDestroy()
@Component({
    selector: 'app-organizations-list',
    templateUrl: './organizations-list.component.html',
    standalone: true,
    styleUrls: ['./organizations-list.component.scss'],
    imports: [
        AppTableComponent,
        MatSortModule,
        MatTableModule,
        EmptyCellDirective,
        I18nPluralPipe,
        DecimalPipe,
        NumberCellDirective,
        NgForOf,
        AvatarBlockModule,
        PageHeaderComponent,
        RouterLink,
        EmptyStateModule,
        MatButtonModule,
        NgIf,
        MatIconModule,
        AnimatedDirective,
        MatChipsModule,
        AddressLinePipe,
    ],
})
export class OrganizationsListComponent implements OnInit, AfterViewInit
{
    @ViewChild(AppTableComponent) tableRef: AppTableComponent<Organization>;
    @ViewChild(MatSort, { static: true }) columnSort: MatSort;

    routerAddButton: RouterButtonValue;

    readonly unitsMapping: I18nMap = {
        '=0': 'No units',
        '=1': '1 unit',
        'other': '# units',
    };
    readonly tableType = TableTypeNames.PMC_COMMUNITIES;
    readonly columnMap: ColumnMap[] = [
        {key: 'mobile', title: 'Community', exportAs: 'community', isMobile: true},
        {key: 'name', title: 'Community', exportAs: 'community'},
        {key: 'admins', title: 'Managers', exportAs: 'managers'},
        {key: 'address', title: 'Address', exportAs: 'address'},
        {key: 'openSubmissionsCount', title: 'Open Submissions', exportAs: 'openSubmissions'},
        {key: 'openViolationsCount', title: 'Open Violations', exportAs: 'openViolations'},
    ];

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _sidenav: SidenavContentService,
            private _activatedRoute: ActivatedRoute,
            private _router: Router,
            private _scrollService: ScrollService,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    ngOnInit() {
        this._scrollService.scrollToTop();

        this.routerAddButton = {
            label: 'Community',
            route: `/pmc/${this.pmcId}/communities/add`,
            ariaLabel: 'Add new community',
        };
    }

    ngAfterViewInit() {
        this.setupTable();
    }

    goToOrganization(event: SelectedEvent<Organization>) {
        return this._router.navigate([event.source.id, 'details'], { relativeTo: this._activatedRoute });
    }

    openFilters(filters: { [key: string]: any }) {
        const data: OrganizationFiltersSidenavInput = {
            filters,
        };

        this._sidenav.open(OrganizationFiltersSidenavComponent, {
            data,
        }).afterClosed().subscribe((result: OrganizationFiltersSidenavOutput) => {
            if (result?.success) {
                return this._router.navigate([], {
                    relativeTo: this._activatedRoute,
                    queryParams: {
                        ...result.filters,
                    },
                });
            }
        });
    }

    private setupTable() {
        this.tableRef.connectDataTable$.pipe(
                switchMap(() => {
                    if (this.pmcId) {
                        return this._propertyManagementCompanyService.getPropertyManagementOrganizations(
                                this.tableRef.searchValue,
                                this.columnSort.active,
                                this.columnSort.direction,
                                this.tableRef.paginator.pageIndex + 1,
                                this.tableRef.paginator.pageSize,
                                this.tableRef.filters,
                        );
                    }

                    return;
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => this.tableRef.data$.next(res),
            error: () => {
                this.tableRef.loading = false;
                this._growler.oops('Unable to load organizations');
            },
        });
    }
}
